import { useCallback, useLayoutEffect, useEffect, useState } from 'react';

import { debounce } from '@pp/core/src/utils/debounce';

import { useLauncherContext } from '../../context/LauncherContext';
import {
  LAUNCHER_BASE_HEIGHT,
  LAUNCHER_BASE_WIDTH,
  MIN_WIDTH_BEFORE_MTB_SWITCH_TO_MOBILE,
  MIN_HEIGHT_BEFORE_MTB_SWITCH_TO_MOBILE,
} from '../../helpers/constants';

export const useLayoutScale = (
  ref: React.RefObject<HTMLDivElement>
): [launcherScale: number, tableScale: number, isMobile: boolean] => {
  const [launcherScale, setLauncherScale] = useState(1);
  const [tableScale, setTableScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const { tables } = useLauncherContext();
  const tablesCount = tables.length;

  const getLayoutScale = useCallback((): void => {
    if (!ref.current || !tablesCount) return;

    if (tablesCount <= 2) {
      // Dont scale launcher
      setLauncherScale(1);
      // Scale the table which is now taking half the width
      setTableScale(Math.min(window.innerWidth / 2 / LAUNCHER_BASE_WIDTH, window.innerHeight / LAUNCHER_BASE_HEIGHT));
    } else {
      // Scale the launcher
      setLauncherScale(Math.min(window.innerWidth / LAUNCHER_BASE_WIDTH, window.innerHeight / LAUNCHER_BASE_HEIGHT));
    }

    // Calculate custom isMobile
    if (innerWidth < MIN_WIDTH_BEFORE_MTB_SWITCH_TO_MOBILE || innerHeight < MIN_HEIGHT_BEFORE_MTB_SWITCH_TO_MOBILE) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [tablesCount, ref]);

  useLayoutEffect(() => {
    // Let the launcher scale Apply before calculating the table scale
    if (tablesCount <= 2 || !ref.current || !ref.current.children) return;

    const _elementRect = ref.current.children[0].getBoundingClientRect();
    setTableScale(Math.min(_elementRect.width / (1920 * launcherScale), _elementRect.height / (1080 * launcherScale)));
  }, [ref, launcherScale, tablesCount]);

  useEffect(() => {
    getLayoutScale();
  }, [getLayoutScale]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const callback = debounce(getLayoutScale, 50);
      callback();
    });

    resizeObserver.observe(document.documentElement);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [getLayoutScale]);

  return [launcherScale, tableScale, isMobile];
};
